// Space Designer (Phase 36) — frontend projection mapper.
//
// The SDS engine works in centimetres. The CRM relational model works in
// canvas pixels (= feet * floor.pixelsPerFoot) + real-world feet. This module
// walks the Scene and produces the `projection` payload (clusters + resources,
// already converted) that the backend reconciler (space-design-projection.ts)
// upserts into space_workstation_clusters / space_resources.
//
// Only BOOKABLE items are projected: rooms (cabins / conference / etc.),
// workstation seats, loose chairs, sofas (meeting), conference tables.
// Walls, measurements, areas, blueprint, plants, doors, fixtures stay in the
// blob only.

import type { Scene, Item, Shape } from "./types";
import { getSeatSlots } from "./workstation-layout";

const CM_PER_FT = 30.48;

export interface ProjectionCluster {
  designItemId: string;
  label: string;
  x: number; y: number;
  rows: number;
  seatsPerRow: number;
  seatWidthFt: number;
  seatLengthFt: number;
  tableLengthFt?: number | null;
  tableDepthFt: number;
  partitionType: string;
}

export interface ProjectionResource {
  designItemId: string;
  clusterDesignItemId?: string | null;
  resourceType: string;
  label: string;
  seatIndex?: number | null;
  capacity?: number;
  x: number; y: number; w: number; h: number;
  rotation?: number;
  widthFt?: number | null;
  lengthFt?: number | null;
  status?: string;
  zone?: string | null;
}

export interface Projection {
  clusters: ProjectionCluster[];
  resources: ProjectionResource[];
}

const isWorkstationShape = (s: Shape): boolean =>
  s === "workstation" || s === "workstation-linear" || s === "workstation-corner" ||
  s === "workstation-l" || s === "workstation-table";

/** CRM resource_type for a room item, by its catalog id. */
// Phase 54 Plan 05 (SPACE-UX-UNIT-KIND-09): branch order matters — first-match-wins
// on substring. `pantry` MUST be checked BEFORE `cafeteria` (both previously folded
// into "Cafeteria", losing the Pantry distinction — 54-RESEARCH.md Pitfall 4).
function roomResourceType(type: string): string | null {
  const t = type.toLowerCase();
  if (t.includes("conference")) return "Conference_Room";
  if (t.includes("reception")) return "Reception";
  if (t.includes("pantry")) return "Pantry"; // MUST be before cafeteria
  if (t.includes("cafeteria")) return "Cafeteria";
  if (t.includes("washroom-female")) return "Washroom_Female";
  if (t.includes("washroom-male")) return "Washroom_Male";
  if (t.includes("store")) return "Store_Room";
  if (t.includes("washbasin")) return "Washbasin";
  if (t.includes("bay")) return "Bay";
  if (t.includes("workstation")) return null; // a workstation-area room is just a zone — not bookable
  return "Private_Cabin"; // director / team / cabin — pre-existing catch-all, out of scope (Pitfall 5)
}

function clusterShapeMeta(shape: Shape, seats: number) {
  switch (shape) {
    case "workstation": return { rows: 2, seatsPerRow: Math.max(1, Math.ceil(seats / 2)) };
    case "workstation-corner": return { rows: 1, seatsPerRow: 1 };
    default: return { rows: 1, seatsPerRow: Math.max(1, seats) };
  }
}

export function computeProjection(scene: Scene, pixelsPerFoot: number): Projection {
  const ppf = pixelsPerFoot > 0 ? pixelsPerFoot : 20;
  const cmToPx = (cm: number) => Math.round((cm / CM_PER_FT) * ppf);
  const cmToFt = (cm: number) => Number((cm / CM_PER_FT).toFixed(2));

  const clusters: ProjectionCluster[] = [];
  const resources: ProjectionResource[] = [];
  const items: Item[] = scene.items ?? [];

  for (const it of items) {
    // ── Workstations (compact) → cluster + virtual seats ──────────────────
    if (it.kind === "furniture" && isWorkstationShape(it.shape)) {
      const seats = Math.max(1, it.seats ?? 1);
      const label = it.label?.trim() || `WS-${it.id.slice(0, 4)}`;
      const meta = clusterShapeMeta(it.shape, seats);
      const slots = getSeatSlots(it.shape, it.w, it.h, seats);
      const seatWcm = slots[0]?.w ?? 45;
      const seatHcm = slots[0]?.h ?? 45;
      clusters.push({
        designItemId: it.id,
        label,
        x: cmToPx(it.x),
        y: cmToPx(it.y),
        rows: meta.rows,
        seatsPerRow: meta.seatsPerRow,
        seatWidthFt: Math.max(0.5, cmToFt(seatWcm)),
        seatLengthFt: Math.max(0.5, cmToFt(seatHcm)),
        tableLengthFt: null,
        tableDepthFt: 2,
        partitionType: meta.rows === 2 ? "Lacquer_Glass" : "None",
      });
      slots.forEach((slot, i) => {
        const topLeftXcm = it.x + slot.cx - slot.w / 2;
        const topLeftYcm = it.y + slot.cy - slot.h / 2;
        resources.push({
          designItemId: `${it.id}#${i}`,
          clusterDesignItemId: it.id,
          resourceType: "Workstation",
          label: `${label}-S${String(i + 1).padStart(2, "0")}`,
          seatIndex: i,
          capacity: 1,
          x: cmToPx(topLeftXcm),
          y: cmToPx(topLeftYcm),
          w: cmToPx(slot.w),
          h: cmToPx(slot.h),
          rotation: Math.round((it.rotation ?? 0) + slot.rot),
          widthFt: cmToFt(slot.w),
          lengthFt: cmToFt(slot.h),
        });
      });
      continue;
    }

    // ── Rooms → one resource (cabin / conference / reception / …) ──────────
    if (it.kind === "room") {
      const rt = roomResourceType(it.type);
      if (!rt) continue;
      resources.push({
        designItemId: it.id,
        resourceType: rt,
        label: it.label?.trim() || `R-${it.id.slice(0, 4)}`,
        capacity: it.seats ?? 1,
        x: cmToPx(it.x), y: cmToPx(it.y), w: cmToPx(it.w), h: cmToPx(it.h),
        rotation: Math.round(it.rotation ?? 0),
        widthFt: cmToFt(it.w), lengthFt: cmToFt(it.h),
        zone: it.teamId ?? null,
      });
      continue;
    }

    // ── Loose chair (incl. exploded seats) → single Workstation seat ───────
    if (it.shape === "chair") {
      resources.push({
        designItemId: it.id,
        resourceType: "Workstation",
        label: it.seatCode?.trim() || it.label?.trim() || `S-${it.id.slice(0, 4)}`,
        capacity: 1,
        x: cmToPx(it.x), y: cmToPx(it.y), w: cmToPx(it.w), h: cmToPx(it.h),
        rotation: Math.round(it.rotation ?? 0),
        widthFt: cmToFt(it.w), lengthFt: cmToFt(it.h),
      });
      continue;
    }

    // ── Sofa → Meeting room; conference table → Conference room ────────────
    if (it.shape === "sofa" || it.shape === "conference" || it.shape === "conference-room") {
      resources.push({
        designItemId: it.id,
        resourceType: it.shape === "sofa" ? "Meeting_Room" : "Conference_Room",
        label: it.label?.trim() || `${it.shape}-${it.id.slice(0, 4)}`,
        capacity: it.seats ?? 1,
        x: cmToPx(it.x), y: cmToPx(it.y), w: cmToPx(it.w), h: cmToPx(it.h),
        rotation: Math.round(it.rotation ?? 0),
        widthFt: cmToFt(it.w), lengthFt: cmToFt(it.h),
      });
      continue;
    }
    // everything else (walls/doors/fixtures/plants/desk-rect/zone…) → blob only
  }

  return { clusters, resources };
}
